import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {useReportTypesQuery} from './useReportTypesQuery';
import {useReportTypesSearch} from './useReportTypesSearch';
import {useReportTypesTableColumns} from './useReportTypesTableColumns';
import {ReportTypesFilters} from './useReportTypesFilter';
import {ReportType} from '../types';

/**
 * Default sort: BY NAME, ascending.
 *
 * ⚠️ This is the one module in this wave that does NOT default to newest-first,
 * and the difference is deliberate. Report types are a CATALOGUE, not an event
 * stream: an operator arrives looking for a specific report by name, not to see
 * what changed most recently. `updated` is also the wrong key on both counts
 * the checklist names — it is mutable (it moves when Stripe revises a report)
 * and it is nullable.
 *
 * `id` would sort identically in practice since names track ids, but `name` is
 * what the first column displays, and the default sort should match what the
 * reader sees.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<ReportType> = {
    columnAccessor: 'name',
    direction: 'asc',
};

/**
 * Which fields a keyword matches against.
 *
 * `defaultColumns` is included because an operator hunting for a report that
 * contains a particular field ("which report has fee_amount?") has no other way
 * to find it — the column list is the substance of a report type.
 */
const matchesKeyword = (item: ReportType, needle: string): boolean => {
    if (!needle) {
        return true;
    }

    const scalars = [item.id, item.name].some(
        value => typeof value === 'string'
            && value.toLowerCase()
                    .includes(needle));

    if (scalars) {
        return true;
    }

    return (item.defaultColumns ?? []).some(
        column => typeof column === 'string'
            && column.toLowerCase()
                     .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored,
 * so a cleared drawer field does not exclude everything.
 */
const matchesFilters = (
    item: ReportType,
    filters: ReportTypesFilters,
): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof ReportType];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ UNLIKE ITS SIBLINGS, sort and filter here cover the WHOLE dataset.
 *
 * `listReportTypes` takes no arguments and returns no `nextToken` — the single
 * response IS the entire catalogue (see useReportTypesQuery for why that is
 * correct rather than a missing feature). So the "client-side filtering only
 * sees the loaded window" caveat that applies to cards, bank-accounts and
 * report-runs does NOT apply here: every row was examined, and a match count
 * would be honest.
 *
 * The footer still states a loaded count rather than a total, because the
 * shared control is the shared control and inventing a second footer for one
 * module would be worse than a slightly conservative message.
 */
export const useReportTypes = (filters: ReportTypesFilters = {}) => {
    const query = useReportTypesQuery();
    const {columns} = useReportTypesTableColumns();
    const {keyword} = useReportTypesSearch();

    // ⚠️ Stripe hands back epoch SECONDS; moment parses a bare number as
    // MILLISECONDS. Normalise ONCE, here, so display and sort agree — and so
    // no column has to cast a number through `as unknown as string`, which is
    // what hid this bug: every row rendered as January 1970.
    const items: ReportType[] = (query.data?.items ?? [])
        .filter(Boolean)
        .map(item => ({
            ...item,
            updatedMillis: typeof item.updated === 'number'
                ? item.updated * 1000
                : undefined,
            dataAvailableStartMillis: typeof item.dataAvailableStart === 'number'
                ? item.dataAvailableStart * 1000
                : undefined,
            dataAvailableEndMillis: typeof item.dataAvailableEnd === 'number'
                ? item.dataAvailableEnd * 1000
                : undefined,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<ReportType>(matched, DEFAULT_SORT_STATUS);

    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full catalogue BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        reload: () => query.refetch(),
    };
};
